import {
  Box,
  Button,
  FormField,
  Input,
  KeyValuePairs,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { toast } from 'sonner';
import * as Yup from 'yup';

import createUser from '../api/createUser';

/**
 * Creates a passwordless user in the given organization. The org is fixed by
 * the page that opens the modal, so it's shown read-only. An email domain
 * outside `org.domains` only warns — contractors and personal emails are
 * legitimate members.
 */
export default function AddUserModal({
  open,
  organization,
  defaultEmail = '',
  onUserAdded = null, // callback to notify parent of new user addition
  onClose,
}) {
  const {
    values, errors, isSubmitting, setFieldValue, handleSubmit,
  } = useFormik({
    initialValues: { email: defaultEmail },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email format').required('Email is required'),
    }),
    onSubmit: async (vals, { setFieldError }) => {
      try {
        const newUser = await createUser({ email: vals.email, orgId: organization.id });
        toast.success(`Created user ${newUser.email}`);
        if (typeof onUserAdded === 'function') {
          onUserAdded(newUser);
        }
        onClose();
      } catch (error) {
        setFieldError('email', error.message);
      }
    },
  });

  const emailDomain = values.email.split('@')[1]?.toLowerCase();
  const domainMismatch = !!emailDomain
    && !(organization.domains || []).some((domain) => domain.toLowerCase() === emailDomain);

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      header="Add New User"
      footer={(
        <Box float="right">
          <Button variant="link" formAction="none" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" formAction="submit" form="create-user-form" loading={isSubmitting}>
            Create
          </Button>
        </Box>
      )}
    >
      <form id="create-user-form" onSubmit={handleSubmit}>
        <SpaceBetween size="l">
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Organization', value: organization.name || '-' },
              { label: 'Domains', value: organization.domains?.join(', ') || '-' },
            ]}
          />

          <FormField
            label="Email"
            errorText={errors.email}
            warningText={domainMismatch && `${emailDomain} is not one of this organization's domains.`}
          >
            <Input
              value={values.email}
              onChange={(e) => setFieldValue('email', e.detail.value)}
              placeholder="Enter user email"
            />
          </FormField>
        </SpaceBetween>
      </form>
    </Modal>
  );
}
AddUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  organization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    domains: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  defaultEmail: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUserAdded: PropTypes.func,
};
